// League trade center: respond to offers, track your proposals,
// and read the full league trade log. Commissioner can veto pending trades.
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { League, Team, Trade } from '../../types';
import BackLink from '../../components/common/BackLink';
import TradeCard from '../../components/league/TradeCard';
import ProposeTradeModal from '../../components/league/ProposeTradeModal';

export default function TradesPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();
  const id = Number(leagueId);

  const [league, setLeague] = useState<League | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [busyTradeId, setBusyTradeId] = useState<number | null>(null);
  const [tradeErrors, setTradeErrors] = useState<Record<number, string>>({});
  const [proposeOpen, setProposeOpen] = useState(false);

  const myTeam = teams.find((t) => t.user_id === user?.id) ?? null;
  const isCommissioner = league?.commissioner_id === user?.id;

  const loadTrades = useCallback(async () => {
    setTrades(await apiService.getLeagueTrades(id));
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setPageError(null);
      try {
        const [lg, tms] = await Promise.all([apiService.getLeague(id), apiService.getTeams(id)]);
        setLeague(lg);
        setTeams(tms);
        await loadTrades();
      } catch (e: any) {
        const msg = e.message || 'Failed to load trades';
        // Membership 403 gets the friendlier spec copy.
        setPageError(
          msg.includes('not a member')
            ? 'You are not a member of this league. Join the league to view and propose trades.'
            : msg
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id, loadTrades]);

  const runAction = async (trade: Trade, action: (tradeId: number) => Promise<Trade>) => {
    setBusyTradeId(trade.id);
    setTradeErrors((errs) => ({ ...errs, [trade.id]: '' }));
    try {
      await action(trade.id);
      await loadTrades();
    } catch (e: any) {
      setTradeErrors((errs) => ({ ...errs, [trade.id]: e.message || 'Action failed' }));
    } finally {
      setBusyTradeId(null);
    }
  };

  const cardProps = (t: Trade, opts: { suppressActions?: boolean } = {}) => ({
    trade: t,
    myTeamId: myTeam?.id ?? null,
    isCommissioner: !!isCommissioner,
    busy: busyTradeId === t.id,
    error: tradeErrors[t.id] || null,
    suppressActions: !!opts.suppressActions,
    onAccept: (tr: Trade) => runAction(tr, apiService.acceptTrade),
    onReject: (tr: Trade) => runAction(tr, apiService.rejectTrade),
    onCancel: (tr: Trade) => runAction(tr, apiService.cancelTrade),
    onVeto: (tr: Trade) => {
      if (window.confirm('Veto this trade? Both teams will be notified.')) {
        runAction(tr, apiService.vetoTrade);
      }
    },
  });

  const needsResponse = trades.filter(
    (t) => t.status === 'pending' && myTeam && t.recipient_team_id === myTeam.id
  );
  const pendingOffers = trades.filter(
    (t) => t.status === 'pending' && myTeam && t.proposer_team_id === myTeam.id
  );
  // A pending trade already rendered in a role section above also appears in
  // the log (intentional — the log is the full record). Suppress the ACTION row
  // (and its inline error) on that second copy so a duplicated still-enabled
  // button can't fire a second POST; the role-section card is the one the user acts on.
  const dedupedIds = new Set([...needsResponse, ...pendingOffers].map((t) => t.id));

  if (loading) {
    return <p className="py-16 text-center text-secondary-500">Loading…</p>;
  }

  if (pageError) {
    return (
      <div>
        <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>
        <p className="mt-6 text-sm text-danger">{pageError}</p>
      </div>
    );
  }

  const section = (testId: string, title: string, rows: Trade[]) => (
    <section data-testid={testId} className="mb-8">
      <h2 className="font-display text-xl uppercase tracking-tight text-ink-900 border-b border-ink-900 pb-2 mb-4">
        {title}
      </h2>
      <div className="space-y-4">
        {rows.map((t) => <TradeCard key={t.id} {...cardProps(t)} />)}
      </div>
    </section>
  );

  return (
    <div>
      <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <div>
          <h1 className="font-display text-3xl uppercase tracking-tight text-ink-900">Trades</h1>
          {league && <p className="text-sm text-secondary-500 mt-1">{league.name}</p>}
        </div>
        {myTeam && (
          <button
            onClick={() => setProposeOpen(true)}
            className="btn px-4 py-2 bg-ink-900 text-paper text-xs font-semibold uppercase tracking-wider hover:bg-ink-900/90"
          >
            Propose Trade
          </button>
        )}
      </div>

      {needsResponse.length > 0 && section('needs-response', 'Needs Your Response', needsResponse)}
      {pendingOffers.length > 0 && section('pending-offers', 'Your Pending Offers', pendingOffers)}

      <section data-testid="trade-log">
        <h2 className="font-display text-xl uppercase tracking-tight text-ink-900 border-b border-ink-900 pb-2 mb-4">
          League Trade Log
        </h2>
        {trades.length === 0 ? (
          <p className="py-12 text-center font-display text-lg uppercase tracking-wider text-secondary-400">
            No trades on record.
          </p>
        ) : (
          <div className="space-y-4">
            {trades.map((t) => (
              <TradeCard key={t.id} {...cardProps(t, { suppressActions: dedupedIds.has(t.id) })} />
            ))}
          </div>
        )}
      </section>

      {proposeOpen && myTeam && (
        <ProposeTradeModal
          leagueId={id}
          myTeam={myTeam}
          teams={teams}
          onClose={() => setProposeOpen(false)}
          onProposed={() => {
            setProposeOpen(false);
            loadTrades();
          }}
        />
      )}
    </div>
  );
}
